/**
 * Utilidad para validación de placas colombianas
 * 
 * Formatos:
 * - Carros (AUTO, CAMIONETA, CAMION): AAA000 (3 letras + 3 números)
 * - Motos: AAA00A (3 letras + 2 números + 1 letra)
 */

// Tipos de vehículo según el formato de placa
export type PlateFormat = 'CARRO' | 'MOTO' | null;

// Tipos de vehículo que corresponden a cada formato
export const CARRO_TYPES = ['AUTO', 'CAMIONETA', 'CAMION'] as const;
export const MOTO_TYPES = ['MOTO'] as const;

// Regex para validar formatos de placa colombiana
const CARRO_PLATE_REGEX = /^[A-Z]{3}[0-9]{3}$/;
const MOTO_PLATE_REGEX = /^[A-Z]{3}[0-9]{2}[A-Z]$/;

/**
 * Valida si una placa tiene formato colombiano válido (carro o moto)
 */
export function isValidColombianPlate(placa: string): boolean {
    const normalizedPlaca = placa.toUpperCase().replace(/[-\s]/g, '');
    return CARRO_PLATE_REGEX.test(normalizedPlaca) || MOTO_PLATE_REGEX.test(normalizedPlaca);
}

/**
 * Detecta el formato de la placa
 * @returns 'CARRO' para placas AAA000, 'MOTO' para AAA00A, null si no es válida
 */
export function getPlateFormat(placa: string): PlateFormat {
    const normalizedPlaca = placa.toUpperCase().replace(/[-\s]/g, '');

    if (CARRO_PLATE_REGEX.test(normalizedPlaca)) {
        return 'CARRO';
    }

    if (MOTO_PLATE_REGEX.test(normalizedPlaca)) {
        return 'MOTO';
    }

    return null;
}

/**
 * Obtiene los tipos de vehículo compatibles con el formato de placa
 * @returns Array de tipos de vehículo compatibles o vacío si no es válida
 */
export function getCompatibleVehicleTypes(placa: string): string[] {
    const format = getPlateFormat(placa);

    if (format === 'CARRO') {
        return [...CARRO_TYPES];
    }

    if (format === 'MOTO') {
        return [...MOTO_TYPES];
    }

    return [];
}

/**
 * Filtra tarifas según el formato de placa
 * @param rates Array de tarifas disponibles
 * @param placa Placa del vehículo
 * @returns Array de tarifas compatibles con el formato de placa
 */
export function filterRatesByPlateFormat<T extends { tipoVehiculo: string; activo: boolean }>(
    rates: T[],
    placa: string
): T[] {
    const compatibleTypes = getCompatibleVehicleTypes(placa);

    if (compatibleTypes.length === 0) {
        // Si no se detecta formato, retornar todas las tarifas activas
        return rates.filter(rate => rate.activo);
    }

    // Filtrar por tipos compatibles y activas
    return rates.filter(rate =>
        rate.activo && compatibleTypes.includes(rate.tipoVehiculo)
    );
}

/**
 * Obtiene información de formato para UI
 */
export function getPlateFormatInfo(placa: string): {
    format: PlateFormat;
    icon: string;
    label: string;
    color: string;
} | null {
    const format = getPlateFormat(placa);

    if (format === 'CARRO') {
        return {
            format,
            icon: 'ri-car-fill',
            label: 'Vehículo',
            color: 'blue'
        };
    }

    if (format === 'MOTO') {
        return {
            format,
            icon: 'ri-motorbike-fill',
            label: 'Motocicleta',
            color: 'purple'
        };
    }

    return null;
}
